import VerifyIcon from "@assets/icons/verify.svg";
import InfoIcon from "@assets/icons/info.svg";
import ProgressIcon from "@assets/icons/progress.svg";
import CheckIcon from "@assets/icons/check.svg";
import WarnIcon from "@assets/icons/warn.svg";
import styled from "@emotion/styled";
import { Button } from "@ui/components/buttons/button";
import { TertiaryButton } from "@ui/components/buttons/tertiary-button";
import { physioMeTabletBreakpoint, row, stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import React, { HTMLAttributes } from "react";
import { ReactJSXElement } from "@emotion/react/types/jsx-namespace";

export interface AlertCardProps extends HTMLAttributes<HTMLDivElement> {
	cardType: CardType;
	message: string;
	title?: string;
	secondaryButtons?: { title: string; onPressed: () => void; loading?: boolean }[];
	textButton?: { text: string; onPressed?: () => void };
	onCrossClicked?: () => void;
}

export enum CardType {
	Info = "Info",
	Warn = "Warn",
	Pending = "Pending",
	Validated = "Validated",
	Warning = "Warning",
}

const CardStyle: Record<CardType, { assetColor: string; backgroundColor: string; icon: ReactJSXElement; padding: string; width: string }> = {
	[CardType.Info]: {
		assetColor: colors.primaryDark,
		backgroundColor: colors.primary10,
		icon: <VerifyIcon width={20} height={20} color={colors.primaryDark} />,
		padding: "16px",
		width: "auto",
	},
	[CardType.Warn]: {
		assetColor: colors.warnDark,
		backgroundColor: colors.warn,
		icon: <InfoIcon width={20} height={20} color={colors.warnDark} />,
		padding: "16px",
		width: "auto",
	},
	[CardType.Warning]: {
		assetColor: colors.grey,
		backgroundColor: colors.primary10,
		icon: <WarnIcon width={20} height={20} color={colors.grey} />,
		padding: "8px 16px",
		width: "100%",
	},
	[CardType.Pending]: {
		assetColor: colors.primaryDark,
		backgroundColor: colors.primary10,
		icon: <ProgressIcon width={20} height={20} color={colors.primaryDark} />,
		padding: "16px",
		width: "auto",
	},
	[CardType.Validated]: {
		assetColor: colors.validationDark,
		backgroundColor: colors.validation10,
		icon: <CheckIcon width={20} height={20} color={colors.validationDark} />,
		padding: "16px",
		width: "auto",
	},
};

export const AlertCard = React.forwardRef<HTMLDivElement, AlertCardProps>(
	({ title, message, cardType, secondaryButtons, textButton, onCrossClicked, ...otherProps }, ref) => (
		<AlertCardContainer cardType={cardType} ref={ref} {...otherProps}>
			<div>{CardStyle[cardType].icon}</div>
			<Container className="alertContainer">
				<Column>
					{title && (
						<CardTitle className="p-size-s-bold" textColor={CardStyle[cardType].assetColor}>
							{title}
						</CardTitle>
					)}
					<CardDescription className="p-size-s" textColor={CardStyle[cardType].assetColor}>
						{message}
					</CardDescription>
				</Column>
				{cardType === CardType.Validated && <DeleteIcon className="pi pi-times" onClick={onCrossClicked} />}
				{secondaryButtons &&
					secondaryButtons.length > 0 &&
					secondaryButtons.map((action, index) => (
						<OneLineButton key={`${action.title}-${index}`} uiStyle="secondary_B" onClick={action.onPressed} loading={action.loading}>
							{action.title}
						</OneLineButton>
					))}
				<LabelContainer>
					{textButton &&
						(textButton.onPressed ? (
							<StyledTextButton onClick={textButton.onPressed} label={textButton.text} />
						) : (
							<Label>{textButton.text}</Label>
						))}
				</LabelContainer>
			</Container>
		</AlertCardContainer>
	),
);
AlertCard.displayName = "AlertCard";

const AlertCardContainer = styled.div<{ cardType: CardType }>`
	${row("XS")}
	padding: ${({ cardType }) => CardStyle[cardType].padding};
	border-radius: 8px;
	background-color: ${({ cardType }) => CardStyle[cardType].backgroundColor};
	width: ${({ cardType }) => CardStyle[cardType].width};
`;

const CardTitle = styled.p<{ textColor: string }>`
	color: ${({ textColor }) => textColor};
`;
const CardDescription = styled.p<{ textColor: string }>`
	color: ${({ textColor }) => textColor};
	white-space: pre-wrap;
`;

const Column = styled.div`
	${stack()}
	flex: 1;
`;

const LabelContainer = styled.div`
	margin-right: 8px;
	white-space: nowrap;
`;

const Label = styled.div`
	color: ${colors.grey};
`;

const StyledTextButton = styled(TertiaryButton)`
	white-space: nowrap;
`;

const Container = styled.div`
	${row("XS", "spacebetween")}
	flex-grow: 1;
	@media ${physioMeTabletBreakpoint} {
		${stack("XS")}
	}
`;

const OneLineButton = styled(Button)`
	white-space: nowrap;
	min-width: max-content;
`;

const DeleteIcon = styled.i`
	cursor: pointer;
	white-space: nowrap;
	min-width: max-content;
`;
